var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/Fonts.ts
import {
  FontStyle as FontStyle2,
  FontUtils as FontUtils2,
  TextUtils
} from "@ballpark/design-creator";
import { computed, makeAutoObservable } from "mobx";

// src/selectors.ts
import { groupBy } from "lodash-es";

// src/utils.ts
import {
  FontStyle,
  FontUtils
} from "@ballpark/design-creator";
import { orderBy } from "lodash-es";
function getFontVariants(fontFamilies2) {
  return Object.keys(fontFamilies2).reduce(
    (fontVariants, fontName) => {
      const mappedVariants = fontFamilies2[fontName].map(
        ({ fontStyle, fontWeight }) => FontUtils.createFontVariant({ fontStyle, fontWeight })
      );
      fontVariants[fontName] = orderBy(
        mappedVariants,
        [(variant) => variant.fontStyle === FontStyle.Normal, "fontWeight"],
        ["desc", "asc"]
      );
      return fontVariants;
    },
    {}
  );
}

// src/selectors.ts
function fontFamilies() {
  return groupBy(this.fontFaces, "fontFamily");
}
function fontVariantsByFamily() {
  return getFontVariants(this.fontFamilies);
}

// src/Fonts.ts
var Fonts = class {
  constructor({ design, fontFaces = [] }) {
    this._design = design;
    this.fontFaces = fontFaces;
    makeAutoObservable(this, {
      _design: false,
      fontFamilies: computed.struct,
      fontVariantsByFamily: computed.struct
    });
  }
  set design(design) {
    this._design = design;
  }
  get fontFamilies() {
    return fontFamilies.call(this);
  }
  get fontVariantsByFamily() {
    return fontVariantsByFamily.call(this);
  }
  /**
   * When changing the font family of text, the new family may not have a matching variant
   * this function sets the font family and updates its variant to the closest matching one in the
   * new font family
   */
  setFamilyAndUpdateVariant(text, familyName) {
    this._design.transact(() => {
      const fontStyle = TextUtils.getFontItalic(text) ? FontStyle2.Italic : FontStyle2.Normal;
      const fontWeight = TextUtils.getFontWeight(text);
      const newVariant = FontUtils2.findClosestVariant(
        this.fontVariantsByFamily[familyName],
        { fontStyle, fontWeight }
      );
      this._design.setFontFamily({ id: text.id, fontFamily: familyName });
      if (newVariant) {
        this.setVariant(text, newVariant);
      }
    });
  }
  /**
   * Sets a font weight and style from a variant
   */
  setVariant(text, variant) {
    this._design.transact(() => {
      const { fontWeight, fontStyle } = variant;
      const italic = fontStyle === FontStyle2.Italic;
      this._design.setFontWeight({ id: text.id, fontWeight });
      this._design.setFontItalic({ id: text.id, italic });
    });
  }
  /**
   * Sets a font weight and style from a variant name using the font's current family
   */
  setVariantByName(text, variantName) {
    this._design.transact(() => {
      const fontFamily = TextUtils.getFontFamily(text);
      const newVariant = this.fontVariantsByFamily[fontFamily].find(
        (variant) => variant.name === variantName
      );
      if (!newVariant)
        return;
      const { fontWeight, fontStyle } = newVariant;
      const italic = fontStyle === FontStyle2.Italic;
      this._design.setFontWeight({ id: text.id, fontWeight });
      this._design.setFontItalic({ id: text.id, italic });
    });
  }
};

// src/hooks/useFontLoaded.ts
import { FontUtils as FontUtils3 } from "@ballpark/design-creator";
import FontFaceObserver from "fontfaceobserver";
import { useState as useState2 } from "react";

// src/react.tsx
import { useDesign } from "@ballpark/design-plugin-react";
import { runInAction } from "mobx";
import {
  createContext,
  useContext,
  useEffect,
  useState
} from "react";
import { useMemoOne } from "use-memo-one";
import { Fragment, jsx } from "react/jsx-runtime";
var FontsContext = createContext(null);
function useFonts() {
  const state = useContext(FontsContext);
  if (state === null)
    throw new Error("useFonts() called outside of FontsProvider");
  return state;
}
function useWeakFonts(options) {
  const design = useDesign();
  const maybeFonts = useContext(FontsContext);
  const fonts = useMemoOne(
    () => maybeFonts != null ? maybeFonts : new Fonts(__spreadValues({ design }, options)),
    [maybeFonts]
  );
  return fonts;
}
function FontsProvider({
  children,
  options = {}
}) {
  const design = useDesign();
  const [fonts] = useState(() => new Fonts(__spreadValues({ design }, options)));
  useEffect(() => {
    runInAction(() => {
      if (design)
        fonts.design = design;
    });
  }, [design, fonts]);
  return /* @__PURE__ */ jsx(FontsContext.Provider, { value: fonts, children });
}
function WeakFontsProvider({
  children,
  options
}) {
  const state = useContext(FontsContext);
  if (state === null) {
    return /* @__PURE__ */ jsx(FontsProvider, { options, children });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/hooks/useFontLoaded.ts
var fontObservers = {};
function useFontLoaded({
  // fontFamily is actually a fontDescriptor as it may be one of multiple ways to describe the font
  fontFamily: fontDescriptor,
  fontWeight,
  fontStyle
}) {
  const { fontFaces } = useWeakFonts();
  const fontFamily = FontUtils3.getFontFamily(fontFaces, fontDescriptor);
  const [, forceUpdate] = useState2(0);
  if (fontFamily === null) {
    return true;
  }
  const fontVariant = `${fontFamily};${fontStyle};${fontWeight}`;
  const fontObserver = fontObservers[fontVariant] || {
    done: false,
    promise: new FontFaceObserver(fontFamily, {
      weight: fontWeight,
      style: fontStyle
    }).load()
  };
  fontObservers[fontVariant] = fontObserver;
  if (fontObserver.done) {
    return true;
  }
  fontObserver.promise.finally(() => {
    fontObservers[fontVariant].done = true;
    forceUpdate((n) => n + 1);
  });
  return false;
}
export {
  Fonts,
  FontsProvider,
  WeakFontsProvider,
  getFontVariants,
  useFontLoaded,
  useFonts,
  useWeakFonts
};
